import { FormattedMessage, useIntl } from 'react-intl';

import { GroupTag } from '../GroupsMenu/GroupTag';
import { groupingMessages } from '../groupingMessages';

import './selectedItemsSummary.scss';

import type { GroupFamily, GroupView } from '../../domain/grouping.types';
import type { GroupingItem } from '../../hooks/useGroupingItems';

export interface SelectedItemsSummaryProps {
  family: GroupFamily;
  /** Visible groups of the family, to show each item's current groups. */
  groups: GroupView[];
  items: GroupingItem[];
}

/** "N POIs selected for grouping:" followed by the selected items. */
export function SelectedItemsSummary({ family, groups, items }: Readonly<SelectedItemsSummaryProps>) {
  const { formatMessage } = useIntl();
  const groupsById = new Map(groups.map(group => [group.id, group]));
  const groupsOf = (item: GroupingItem) => [...new Set(item.groupIds)].flatMap(id => groupsById.get(id) ?? []);

  return (
    <section className="selectedItemsSummary">
      <p className="selectedItemsSummary__caption">
        <FormattedMessage
          {...groupingMessages.selectedForGrouping}
          values={{
            bold: chunks => <strong className="selectedItemsSummary__count">{chunks}</strong>,
            items: formatMessage(groupingMessages.familyItemsCount, { count: items.length, family })
          }}
        />
      </p>
      <ul className="selectedItemsSummary__list">
        {items.map(item => (
          <li className="selectedItemsSummary__item" key={item.id}>
            <div className="selectedItemsSummary__text">
              <span className="selectedItemsSummary__name">{item.name}</span>
              {item.detail && <span className="selectedItemsSummary__detail">{item.detail}</span>}
            </div>
            <GroupTag groups={groupsOf(item)} id={`selectedItemsSummary-groups-${item.id}`} />
          </li>
        ))}
      </ul>
    </section>
  );
}
